// Biome metadata used by TileInfoPanel — icon + display name + traversable flag.
// Mirrors `BiomeKind` in packages/sim/src/components.rs.

import {
  CircleDot,
  type LucideIcon,
  Mountain,
  Snowflake,
  Sprout,
  Sun,
  TreePine,
  Trees,
  Waves,
} from 'lucide-react';
import type { MessageKey, Translate } from '../../i18n/index.js';

export interface BiomeMeta {
  readonly displayName: string;
  readonly Icon: LucideIcon;
  readonly traversable: boolean;
}

const META: Record<string, BiomeMeta> = {
  plains: { displayName: 'Plains', Icon: Sprout, traversable: true },
  forest: { displayName: 'Forest', Icon: TreePine, traversable: true },
  hills: { displayName: 'Hills', Icon: Trees, traversable: true },
  mountain: { displayName: 'Mountain', Icon: Mountain, traversable: false },
  desert: { displayName: 'Desert', Icon: Sun, traversable: true },
  tundra: { displayName: 'Tundra', Icon: Snowflake, traversable: true },
  coast: { displayName: 'Coast', Icon: Waves, traversable: true },
  ocean: { displayName: 'Ocean', Icon: Waves, traversable: false },
};

const FALLBACK: BiomeMeta = {
  displayName: 'Unknown',
  Icon: CircleDot,
  traversable: false,
};

export function biomeMeta(biome: string | undefined): BiomeMeta {
  if (!biome) return FALLBACK;
  return META[biome] ?? FALLBACK;
}

/** Localised display name for a biome id; unknown ids get "Unknown". */
export function biomeLabel(t: Translate, biome: string | undefined): string {
  return biome && biome in META ? t(`biome.${biome}` as MessageKey) : t('biome.unknown');
}
